import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import type { TaskeyeHeroApi } from './taskeyeHeroAnimation';

// The animation is light-only; this matches its poster and first frame in both colour modes.
const HERO_BACKGROUND = '#EEF8F4';

// Fetch the animation chunk once the page is idle so it never competes with first paint.
function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(callback, { timeout: 2000 });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(callback, 200);
  return () => window.clearTimeout(handle);
}

export function TaskeyeHeroVideo() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let heroApi: TaskeyeHeroApi | null = null;
    let cancelled = false;
    const cancelIdle = whenIdle(() => {
      import('./taskeyeHeroAnimation')
        .then(({ mountTaskeyeHero }) => {
          if (cancelled || !hostRef.current) return;
          heroApi = mountTaskeyeHero(hostRef.current);
        })
        .catch(error => console.error('TaskEye hero animation failed to load; showing poster', error));
    });
    return () => {
      cancelled = true;
      cancelIdle();
      heroApi?.destroy();
    };
  }, []);

  return (
    <Box
      ref={hostRef}
      data-testid="landing-hero-video"
      w="full"
      // Phones get the animation's native 9:16 portrait composition (phone + live report stacked).
      // Width is capped (not height) so the box stays exactly 9:16 and never exceeds 85% of a short screen.
      sx={{ aspectRatio: { base: '9 / 16', md: '16 / 9' } }}
      maxW={{ base: 'calc(85vh * 9 / 16)', md: 'none' }}
      mx="auto"
      bg={HERO_BACKGROUND}
      borderRadius="2xl"
      overflow="hidden"
      borderWidth="1px"
      borderColor="border-default"
      boxShadow="xl"
    >
      <HeroPoster />
    </Box>
  );
}

// Static first frame (logo lockup), shown until the animation mounts or if it fails to load.
function HeroPoster() {
  return (
    <svg
      data-testid="landing-hero-poster"
      viewBox="0 0 1600 900"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="TaskEye. No app needed — just send a message."
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <defs>
        <radialGradient id="tehPosterGlow" cx="50%" cy="46%" r="55%">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset=".55" stopColor="#F1FAF7" />
          <stop offset="1" stopColor="#E4F4EF" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1600" height="900" fill="url(#tehPosterGlow)" />
      <g transform="translate(596 318)">
        <path
          d="M6 32C14 18.5 23 12 32 12s18 6.5 26 20c-8 13.5-17 20-26 20S14 45.5 6 32z"
          transform="scale(1.3)"
          fill="none"
          stroke="#3A9C87"
          strokeWidth="4.5"
          strokeLinejoin="round"
        />
        <circle cx="41.6" cy="41.6" r="14.3" fill="#60C3AE" />
        <path
          d="M35.1 42l4.4 4.4 8.3-9.1"
          fill="none"
          stroke="#0F2E2A"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <text
          x="104"
          y="60"
          fontFamily="system-ui, sans-serif"
          fontWeight="600"
          fontSize="61"
          letterSpacing="-1.8"
          fill="#0F2E2A"
        >
          TaskEye
        </text>
      </g>
      <text
        x="800"
        y="520"
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontWeight="600"
        fontSize="68"
        letterSpacing="-2"
        fill="#0F2E2A"
      >
        No app needed — just send a message.
      </text>
    </svg>
  );
}
